import type { Tagged } from 'type-fest';
import type { Account } from '../account';
import { MutableEntity } from '../common';
import { UniqueId } from '../UniqueId';
import { Entitlements as _Entitlements, type EntitlementRecords } from './entitlements';
import { _Role } from './role';

// 200万回生成したときの衝突確率が1%
// こちらのサイトの計算で計算 => https://zelark.github.io/nano-id-cc/
const uuidGenerator = UniqueId.GetFactory(8);

export type Organization = MutableEntity & {
  id: Organization.Id;
  name: string;
  profileImageSrc: string;
  mfaRequired: boolean;
  entitlements: Organization.Entitlements;
  roles: Organization.Role[];
};
export namespace Organization {
  export type Id = Tagged<UniqueId, 'organizationId'>;
  export namespace Id {
    export const Generate = () => uuidGenerator.generate() as Id;
    export const FromString = (id: string): Id => uuidGenerator.FromString(id) as Id;
  }

  export import Entitlements = _Entitlements;

  export type Props = MutableEntity.Props & {
    id: string;
    name: string;
    profileImageSrc: string;
    mfaRequired?: boolean;
    entitlements?: unknown;
    roles: Role.Props[];
  };

  export const Construct = (props: Props): Organization => {
    return {
      ...MutableEntity.Construct(props),
      id: Id.FromString(props.id),
      name: props.name,
      profileImageSrc: props.profileImageSrc,
      mfaRequired: props.mfaRequired ?? false,
      entitlements: Entitlements.Construct(props.entitlements),
      roles: props.roles.map(Role.Construct),
    };
  };

  export namespace Create {
    export type Props = Pick<Organization, 'name' | 'profileImageSrc'> & Partial<Pick<Organization, 'mfaRequired'>>;
    export type Params = {
      operatorId: Account.Id;
      props: Props;
    };
  }
  export const Create = (params: Create.Params): Organization => {
    const { operatorId, props } = params;
    const id = Id.Generate();
    return {
      ...MutableEntity.Create({ operatorId }),
      id,
      name: props.name,
      profileImageSrc: props.profileImageSrc,
      mfaRequired: props.mfaRequired ?? false,
      entitlements: Entitlements.Default(),
      roles: Role.CreatePresets(),
    };
  };

  export import Role = _Role;

  export namespace Update {
    export type UpdatableKey = 'name' | 'profileImageSrc' | 'mfaRequired' | 'entitlements';
    export type UpdateProps = Partial<Pick<Organization, 'name' | 'profileImageSrc' | 'mfaRequired'>> & {
      entitlements?: EntitlementRecords;
    };
    export type Params = {
      operatorId: Account.Id;
      cur: Organization;
      updateProps: UpdateProps;
    };
  }
  export const Update = ({ operatorId, cur, updateProps }: Update.Params): Organization => {
    const { entitlements: entitlementsUpdate, ...rest } = updateProps;
    const entitlements =
      entitlementsUpdate !== undefined ? Entitlements.MergeRecordsUpdate(cur.entitlements, entitlementsUpdate) : cur.entitlements;
    return {
      ...cur,
      ...MutableEntity.Update({ cur, operatorId }),
      ...rest,
      entitlements,
    };
  };

  export const getRole = (params: { organization: Organization; roleId: Role.Id }): Role | undefined => {
    return params.organization.roles.find((role) => role.id === params.roleId);
  };

  export const getAdminRoleOrFirst = (organization: Organization): Role => {
    const role = organization.roles.find((role) => role.key === 'admin') ?? organization.roles[0];
    if (!role) {
      throw new Error('Organization has no roles');
    }
    return role;
  };
}
